// modules/WorldMap.js（精简版）
import DebugHelper from './DebugHelper.js';
import { GAME_CONSTANTS } from './GameConstants.js';

export default class WorldMap {
    constructor(scene) {
        this.scene = scene;
        this.worldBounds = null;
        this.obstacles = null;
        this.debugLayer = null;
        this.pushableObjects = [];
        this.isChunked = false;
        this.worldMeta = null;
        this.loadedChunks = new Map();

        this._chunkOffsetX = 0;
        this._chunkOffsetY = 0;
        this._activeChunkJson = null;
        this._activeChunkKey = null;
        this._chunkObjects = null;
    }

    getActiveMapJson() {
        return this._activeChunkJson || this.scene.worldMapJson;
    }

    tileToWorldX(tileX) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        return this._chunkOffsetX + tileX * tileSize + tileSize / 2;
    }

    tileToWorldY(tileY) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        return this._chunkOffsetY + tileY * tileSize + tileSize / 2;
    }

    tileAreaToWorldX(tileX) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        return this._chunkOffsetX + tileX * tileSize;
    }

    tileAreaToWorldY(tileY) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        return this._chunkOffsetY + tileY * tileSize;
    }

    trackChunkObject(gameObject) {
        if (this._chunkObjects && gameObject) {
            this._chunkObjects.push(gameObject);
        }
        return gameObject;
    }

    isChunkLoaded(chunkKey) {
        return this.loadedChunks.has(chunkKey);
    }

    /**
     * 初始化分块大地图（不创建具体区块内容）
     */
    createChunkedWorld(meta) {
        this.isChunked = true;
        this.worldMeta = meta;

        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const mapWidth = meta.worldTileWidth * tileSize;
        const mapHeight = meta.worldTileHeight * tileSize;

        this.setupWorldBounds(mapWidth, mapHeight);
        this.initializeObstacleSystem();
        DebugHelper.log(`WorldMap: 分块大地图就绪 (${meta.gridCols}x${meta.gridRows})`);
    }

    /**
     * 加载单个区块
     */
    loadChunk(gridX, gridY, chunkJson, chunkKey) {
        if (this.loadedChunks.has(chunkKey)) {
            this.unloadChunk(chunkKey);
        }

        const meta = this.worldMeta;
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const chunkPixelW = meta.chunkWidth * tileSize;
        const chunkPixelH = meta.chunkHeight * tileSize;
        const offsetX = gridX * meta.chunkWidth * tileSize;
        const offsetY = gridY * meta.chunkHeight * tileSize;
        const objects = [];

        if (chunkJson.background?.texture) {
            const bg = this.scene.add.tileSprite(
                offsetX,
                offsetY,
                chunkPixelW,
                chunkPixelH,
                chunkJson.background.texture
            );
            bg.setOrigin(0, 0);
            bg.setDepth(0);
            bg.setData('chunkKey', chunkKey);
            objects.push(bg);
        }

        this._chunkOffsetX = offsetX;
        this._chunkOffsetY = offsetY;
        this._activeChunkJson = chunkJson;
        this._activeChunkKey = chunkKey;
        this._chunkObjects = objects;

        this.createMapElements();

        const transitionCount = (this.scene.transitionPoints || []).filter((point) => point.chunkKey === chunkKey).length;

        this.loadedChunks.set(chunkKey, {
            gridX,
            gridY,
            chunkJson,
            objects: [...this._chunkObjects],
            transitionCount
        });

        this._chunkOffsetX = 0;
        this._chunkOffsetY = 0;
        this._activeChunkJson = null;
        this._activeChunkKey = null;
        this._chunkObjects = null;

        DebugHelper.log(`WorldMap: 区块 ${chunkKey} 创建完成，对象 ${objects.length} 个`);
    }

    unloadChunk(chunkKey) {
        const record = this.loadedChunks.get(chunkKey);
        if (!record) return;

        record.objects.forEach((obj) => {
            if (obj?.destroy) {
                obj.destroy();
            }
        });

        if (this.obstacles) {
            const children = [...this.obstacles.getChildren()];
            children.forEach((obstacle) => {
                if (obstacle?.getData?.('chunkKey') === chunkKey) {
                    this.obstacles.remove(obstacle, true, true);
                }
            });
        }

        this.pushableObjects = this.pushableObjects.filter((obj) => obj?.getData?.('chunkKey') !== chunkKey);

        if (this.scene.transitionPoints) {
            this.scene.transitionPoints = this.scene.transitionPoints.filter((point) => point.chunkKey !== chunkKey);
        }

        this.loadedChunks.delete(chunkKey);
    }

    unloadAllChunks() {
        [...this.loadedChunks.keys()].forEach((chunkKey) => this.unloadChunk(chunkKey));
    }

    /**
     * 主地图创建方法
     */
    createMap() {
        const mapJson = this.getActiveMapJson();
        DebugHelper.log('WorldMap: 创建' + mapJson.name + '地图...');

        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const mapWidth = mapJson.tileWidth * tileSize;
        const mapHeight = mapJson.tileHeight * tileSize;

        // 1. 设置相机和世界边界
        this.setupWorldBounds(mapWidth, mapHeight);

        // 2. 初始化障碍系统
        this.initializeObstacleSystem();

        // 3. 创建背景
        this.createBackground(mapWidth, mapHeight);

        // 4. 按顺序创建地图元素
        this.createMapElements();

        // 5. 输出障碍物统计
        this.logObstacleStatistics();

        DebugHelper.log('WorldMap: 地图创建完成');
    }

    // ========== 以下方法保持不变 ==========

    setupWorldBounds(mapWidth, mapHeight) {
        // 获取屏幕尺寸
        const screenWidth = this.scene.cameras.main.width;
        const screenHeight = this.scene.cameras.main.height;
        
        // 如果地图比屏幕小，需要调整相机边界以允许居中显示
        // 相机边界应该至少是屏幕大小，这样相机才能居中显示地图
        const boundsWidth = Math.max(mapWidth, screenWidth);
        const boundsHeight = Math.max(mapHeight, screenHeight);
        
        // 计算偏移量，使地图居中
        const offsetX = mapWidth < screenWidth ? (screenWidth - mapWidth) / 2 : 0;
        const offsetY = mapHeight < screenHeight ? (screenHeight - mapHeight) / 2 : 0;
        
        // 设置相机边界（允许相机超出地图范围，以便居中显示）
        this.scene.cameras.main.setBounds(-offsetX, -offsetY, boundsWidth, boundsHeight);
        
        this.worldBounds = {
            minX: 0,
            maxX: mapWidth,
            minY: 0,
            maxY: mapHeight,
            margin: this.getActiveMapJson().margin || 32,
            width: mapWidth,
            height: mapHeight,
            // 保存居中信息，供相机使用
            offsetX: offsetX,
            offsetY: offsetY,
            needsCentering: mapWidth < screenWidth || mapHeight < screenHeight
        };
    }

    initializeObstacleSystem() {
        this.obstacles = this.scene.physics.add.group({
            immovable: true,
            allowGravity: false
        });

        this.debugLayer = this.scene.add.graphics();
        this.debugLayer.setDepth(100);
        this.debugLayer.setAlpha(0.3);
        this.debugLayer.setVisible(false);

        this.scene.input.keyboard.on('keydown-F2', () => {
            this.debugLayer.setVisible(!this.debugLayer.visible);
        });
    }

    createMapElements() {
        const json = this.getActiveMapJson();

        if (json.naturalElements) this.addNaturalElements();
        if (json.towns) this.createTowns();
        if (json.caves) this.createCaves();
        if (json.buildings) this.createBuildings();
        if (json.tankcenter) this.createTankCenter();
    }

    addObstacle(x, y, texture, options = {}) {
        if (!this.obstacles) {
            DebugHelper.error('障碍组未初始化');
            return null;
        }

        const {
            name = '未命名障碍',
            width = 30,
            height = 30,
            offsetX = 0,
            offsetY = 0,
            debugColor = 0xff0000,
            showDebug = false
        } = options;

        try {
            const obstacleSprite = this.obstacles.create(x, y, texture);

            if (!obstacleSprite) {
                DebugHelper.error(`创建障碍物失败: ${name}`);
                return null;
            }

            obstacleSprite.setOrigin(0.5);
            obstacleSprite.setImmovable(true);
            obstacleSprite.setVisible(true);
            obstacleSprite.setSize(width, height);
            obstacleSprite.setOffset(offsetX, offsetY);
            obstacleSprite.refreshBody();

            obstacleSprite.setData('name', name);
            obstacleSprite.setData('isObstacle', true);
            if (this._activeChunkKey) {
                obstacleSprite.setData('chunkKey', this._activeChunkKey);
            }

            if (showDebug && this.debugLayer) {
                this.drawDebugRect(x, y, width, height, debugColor);
            }

            return obstacleSprite;

        } catch (error) {
            DebugHelper.error(`添加障碍物时出错: ${name}`, error);
            return null;
        }
    }

    createBackground(mapWidth, mapHeight) {
        const mapJson = this.getActiveMapJson();
        if (!mapJson.background) return;

        const { texture, tileSize = GAME_CONSTANTS.TILE_SIZE } = mapJson.background;

        this.backgroundTileSprite = this.trackChunkObject(this.scene.add.tileSprite(
            this._chunkOffsetX,
            this._chunkOffsetY,
            mapWidth,
            mapHeight,
            texture
        ));

        // 设置原点在左上角，这样位置(0,0)就是地图的左上角
        this.backgroundTileSprite.setOrigin(0, 0);

        // 设置深度，确保背景在最底层
        this.backgroundTileSprite.setDepth(0);

        DebugHelper.log(`WorldMap: 使用TileSprite创建背景 (${mapWidth}x${mapHeight})，替代了 ${Math.ceil(mapWidth / tileSize) * Math.ceil(mapHeight / tileSize)} 个图块`);
    }

    createTowns() {
        const towns = this.getActiveMapJson().towns;

        towns.forEach(town => {
            this.addTown(town);
        });

    }


    addPositionsBasedElement(element, options = {}) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const {
            obstacle = false,
            depth = 0,
            allPositionsAsTransition = false  // towns: 所有位置都是传送点; buildings/tankcenter: 只有门是传送点
        } = options;

        const doorPositions = [];

        element.positions.forEach(pos => {
            const x = this.tileToWorldX(pos.tileX);
            const y = this.tileToWorldY(pos.tileY);

            this.trackChunkObject(this.scene.add.image(x, y, pos.texture)
                .setOrigin(0.5)
                .setDepth(depth));

            // 如果不是门，添加障碍物
            if (obstacle && !pos.door) {
                this.addObstacle(x, y, pos.texture, {
                    name: `${element.name}_${pos.tileX}_${pos.tileY}`,
                    width: 30,
                    height: 30
                });
            }

            // 记录所有门的位置（支持同一建筑多个门）
            if (pos.door) {
                doorPositions.push({
                    x,
                    y,
                    targetX: pos.targetX,
                    targetY: pos.targetY
                });
            }
        });

        // 创建传送点
        if (element.map_name) {
            if (!this.scene.transitionPoints) this.scene.transitionPoints = [];

            const transitionWidth = (element.transitionTileWidth || 1) * tileSize;
            const transitionHeight = (element.transitionTileHeight || 1) * tileSize;

            if (allPositionsAsTransition) {
                element.positions.forEach(pos => {
                    const x = this.tileToWorldX(pos.tileX);
                    const y = this.tileToWorldY(pos.tileY);

                    this.scene.transitionPoints.push({
                        x, y,
                        width: transitionWidth,
                        height: transitionHeight,
                        targetScene: element.map_name,
                        targetX: pos.targetX ?? element.targetX,
                        targetY: pos.targetY ?? element.targetY,
                        sound: element.sound || 'into',
                        chunkKey: this._activeChunkKey || null
                    });
                });
            } else if (doorPositions.length > 0) {
                doorPositions.forEach((door) => {
                    this.scene.transitionPoints.push({
                        x: door.x,
                        y: door.y,
                        width: transitionWidth,
                        height: transitionHeight,
                        targetScene: element.map_name,
                        targetX: door.targetX ?? element.targetX ?? 0,
                        targetY: door.targetY ?? element.targetY ?? 0,
                        sound: element.sound || 'into',
                        chunkKey: this._activeChunkKey || null
                    });
                });
            }
        }
    }

    addTown(town) {
        this.addPositionsBasedElement(town, {
            obstacle: false,
            depth: 0,
            allPositionsAsTransition: true  // 所有位置都是传送点
        });

    }

    createCaves() {
        const caves = this.getActiveMapJson().caves || [];

        caves.forEach(cave => {
            this.addCave(cave);
        });

    }

    addCave(cave) {
        this.addPositionsBasedElement(cave, {
            obstacle: false,
            depth: 0,
            allPositionsAsTransition: true
        });

    }


    createBuildings() {
        const buildings = this.getActiveMapJson().buildings || [];
        const tileSize = GAME_CONSTANTS.TILE_SIZE;

        buildings.forEach(building => {
            this.addBuilding(building);
        });
    }

    addBuilding(building) {
        const obstacle = building.obstacle !== undefined ? building.obstacle : true;

        this.addPositionsBasedElement(building, {
            obstacle,
            depth: 5,
            allPositionsAsTransition: true  // 只有门是传送点
        });

    }



    createTankCenter() {
        const tankcenters = this.getActiveMapJson().tankcenter || [];

        tankcenters.forEach(tankcenter => {
            this.addTankCenter(tankcenter);
        });
    }

    addTankCenter(tankcenter) {
        const obstacle = tankcenter.obstacle !== undefined ? tankcenter.obstacle : true;

        this.addPositionsBasedElement(tankcenter, {
            obstacle,
            depth: 5,
            allPositionsAsTransition: false  // 只有门是传送点
        });
    }



    addNaturalElements() {
        const elements = this.getActiveMapJson().naturalElements || [];
        const tileSize = GAME_CONSTANTS.TILE_SIZE;

        elements.forEach(element => {
            if (element.road) {
                // 道路格式（起点和终点，自动生成中间点）
                this.addRoadElement(element.road, tileSize);
            } else if (element.tileX1 !== undefined && element.tileY1 !== undefined &&
                element.tileX2 !== undefined && element.tileY2 !== undefined) {
                // 线性填充格式（从起点到终点填充一排元素）
                const texture = element.texture || element.textures;
                this.addLinearElement(element, texture, element.obstacle || false, tileSize);
            } else if (element.fill) {
                // 区域填充格式（支持水域等）
                const texture = element.texture || element.textures;
                this.addNaturalElementArea(element.fill, texture, element.obstacle || false, tileSize);
            } else if (element.positions) {
                // 批量放置格式
                element.positions.forEach(pos => {
                    const x = this.tileToWorldX(pos.tileX);
                    const y = this.tileToWorldY(pos.tileY);
                    const texture = pos.texture || pos.textures || element.texture || element.textures;
                    const obstacle = pos.obstacle !== undefined ? pos.obstacle : (element.obstacle || false);
                    this.addSingleNaturalElement(x, y, texture, obstacle);
                });
            } else if (element.tileX !== undefined && element.tileY !== undefined) {
                const x = this.tileToWorldX(element.tileX);
                const y = this.tileToWorldY(element.tileY);
                const texture = element.texture || element.textures;
                const push = element.push || false;
                this.addSingleNaturalElement(x, y, texture, element.obstacle || false, push);
            }
        });
    }

    /**
     * 线性填充元素（从起点到终点填充一排）
     * @param {Object} element - 元素配置 { tileX1, tileY1, tileX2, tileY2, texture, obstacle }
     * @param {string|Array} texture - 纹理名称或纹理数组
     * @param {boolean} obstacle - 是否为障碍物
     * @param {number} tileSize - 图块大小
     */
    addLinearElement(element, texture, obstacle, tileSize) {
        const x1 = this.tileToWorldX(element.tileX1);
        const y1 = this.tileToWorldY(element.tileY1);
        const x2 = this.tileToWorldX(element.tileX2);
        const y2 = this.tileToWorldY(element.tileY2);

        const length = Phaser.Math.Distance.Between(x1, y1, x2, y2);
        const segmentCount = Math.ceil(length / tileSize);

        for (let i = 0; i <= segmentCount; i++) {
            const progress = segmentCount > 0 ? i / segmentCount : 0;
            const x = x1 + (x2 - x1) * progress;
            const y = y1 + (y2 - y1) * progress;
            this.addSingleNaturalElement(x, y, texture, obstacle);
        }
    }

    /**
     * 添加道路元素（从起点到终点自动生成道路段）
     * @param {Object} road - 道路配置 { tileX1, tileY1, tileX2, tileY2, texture }
     * @param {number} tileSize - 图块大小
     */
    addRoadElement(road, tileSize) {
        const x1 = this.tileToWorldX(road.tileX1);
        const y1 = this.tileToWorldY(road.tileY1);
        const x2 = this.tileToWorldX(road.tileX2);
        const y2 = this.tileToWorldY(road.tileY2);

        const length = Phaser.Math.Distance.Between(x1, y1, x2, y2);
        const angle = Phaser.Math.Angle.Between(x1, y1, x2, y2);
        const segmentCount = Math.ceil(length / tileSize);

        // 自动判断道路方向
        let roadTexture = road.texture;
        if (!roadTexture) {
            if (Math.abs(angle) < Math.PI / 4 || Math.abs(angle) > 3 * Math.PI / 4) {
                roadTexture = '公路水平段';
            } else {
                roadTexture = '公路垂直段';
            }
        }

        for (let i = 0; i < segmentCount; i++) {
            const progress = i / segmentCount;
            const x = x1 + (x2 - x1) * progress;
            const y = y1 + (y2 - y1) * progress;
            this.addSingleNaturalElement(x, y, roadTexture, false);
        }
    }

    /**
     * 添加单个自然元素
     * @param {number} x - 像素X坐标
     * @param {number} y - 像素Y坐标
     * @param {string|Array} texture - 纹理名称或纹理数组（用于动画）
     * @param {boolean} obstacle - 是否为障碍物
     * @param {boolean} push - 是否可推动
     */
    addSingleNaturalElement(x, y, texture, obstacle, push = false) {
        // 支持纹理数组（用于动画，如水域）
        if (Array.isArray(texture)) {
            const sprite = this.trackChunkObject(this.scene.add.sprite(x, y, texture[0]));
            sprite.setOrigin(0.5);

            // 创建动画
            const animKey = `anim_${texture.join('_')}`;
            if (!this.scene.anims.exists(animKey)) {
                this.scene.anims.create({
                    key: animKey,
                    frames: texture.map(t => ({ key: t })),
                    frameRate: 1,
                    repeat: -1
                });
            }
            sprite.play(animKey);

            if (obstacle && this.obstacles) {
                // 将 Sprite 直接添加到障碍组
                sprite.setData('name', `自然_${texture[0]}`);
                sprite.setData('isObstacle', true);
                if (this._activeChunkKey) {
                    sprite.setData('chunkKey', this._activeChunkKey);
                }
                sprite.setSize(30, 30);
                if (sprite.body) {
                    sprite.body.setImmovable(!push); // 可推动的物体不是不可移动的
                    sprite.refreshBody();
                }
                this.obstacles.add(sprite);
                
                // 如果是可推动的，保存到 pushableObjects
                if (push) {
                    sprite.setData('isPushable', true);
                    sprite.setData('originalX', x);
                    sprite.setData('originalY', y);
                    this.pushableObjects.push(sprite);
                }
            }
        } else {
            // 单个纹理
            let elementSprite;
            
            if (push) {
                // 可推动的物体使用 sprite，以便可以移动
                elementSprite = this.trackChunkObject(this.scene.physics.add.sprite(x, y, texture));
                elementSprite.setOrigin(0.5);
                elementSprite.setData('isPushable', true);
                elementSprite.setData('originalX', x);
                elementSprite.setData('originalY', y);
                // 保存物体名称（用于事件触发）
                elementSprite.setData('name', texture);
                this.pushableObjects.push(elementSprite);
            } else {
                // 不可推动的物体使用 image
                this.trackChunkObject(this.scene.add.image(x, y, texture).setOrigin(0.5));
            }

            if (obstacle) {
                if (push && elementSprite) {
                    // 可推动的障碍物
                    // 对于可推动物体，使用texture作为name（用于事件触发匹配）
                    // 如果已经有name数据，则保留；否则使用texture
                    if (!elementSprite.getData('name')) {
                        elementSprite.setData('name', texture);
                    }
                    elementSprite.setData('isObstacle', true);
                    if (this._activeChunkKey) {
                        elementSprite.setData('chunkKey', this._activeChunkKey);
                    }
                    elementSprite.setSize(30, 30);
                    if (elementSprite.body) {
                        elementSprite.body.setImmovable(false); // 可推动
                        elementSprite.refreshBody();
                    }
                    this.obstacles.add(elementSprite);
                } else {
                    // 不可推动的障碍物
                    this.addObstacle(x, y, texture, {
                        name: `自然_${texture}`,
                        width: 30,
                        height: 30
                    });
                }
            }
        }
    }

    /**
     * 区域填充自然元素
     * @param {Object} area - 区域配置 { tileX, tileY, tileWidth, tileHeight }
     * @param {string|Array} texture - 纹理名称或纹理数组（用于动画）
     * @param {boolean} obstacle - 是否为障碍物
     * @param {number} tileSize - 图块大小
     */
    addNaturalElementArea(area, texture, obstacle, tileSize) {
        const startX = this.tileAreaToWorldX(area.tileX);
        const startY = this.tileAreaToWorldY(area.tileY);
        const width = area.tileWidth * tileSize;
        const height = area.tileHeight * tileSize;

        const tilesX = Math.ceil(width / tileSize);
        const tilesY = Math.ceil(height / tileSize);

        for (let i = 0; i < tilesX; i++) {
            for (let j = 0; j < tilesY; j++) {
                const x = startX + i * tileSize + tileSize / 2;
                const y = startY + j * tileSize + tileSize / 2;
                this.addSingleNaturalElement(x, y, texture, obstacle);
            }
        }
    }


    setupCollision(sprite) {
        if (!sprite) {
            DebugHelper.error('WorldMap: setupCollision - sprite为null');
            return;
        }

        if (!this.obstacles) {
            DebugHelper.error('WorldMap: setupCollision - obstacles组未初始化');
            return;
        }


        this.scene.physics.add.collider(sprite, this.obstacles, (spriteObj, obstacle) => {
            this.handleCollision(spriteObj, obstacle);
        });
    }

    handleCollision(sprite, obstacle) {
        sprite.setVelocity(0);

        // 可选：播放音效
        // try {
        //     const sound = this.scene.sound.add('obstacle', { volume: 0.2 });
        //     sound.play();
        // } catch (error) {
        //     // 静默处理
        // }
    }

    checkWorldBounds(sprite) {
        if (!sprite || !this.worldBounds) return;

        const { minX, maxX, minY, maxY, margin } = this.worldBounds;

        if (sprite.x < minX + margin) {
            sprite.x = minX + margin;
            if (sprite.body && sprite.body.velocity) sprite.setVelocityX(0);
        }

        if (sprite.x > maxX - margin) {
            sprite.x = maxX - margin;
            if (sprite.body && sprite.body.velocity) sprite.setVelocityX(0);
        }

        if (sprite.y < minY + margin) {
            sprite.y = minY + margin;
            if (sprite.body && sprite.body.velocity) sprite.setVelocityY(0);
        }

        if (sprite.y > maxY - margin) {
            sprite.y = maxY - margin;
            if (sprite.body && sprite.body.velocity) sprite.setVelocityY(0);
        }
    }

    drawDebugRect(x, y, width, height, color = 0xff0000) {
        this.debugLayer.lineStyle(1, color);
        this.debugLayer.strokeRect(x - width / 2, y - height / 2, width, height);
    }

    logObstacleStatistics() {
        if (!this.obstacles) {
            DebugHelper.log('障碍系统未初始化');
            return;
        }

        const count = this.obstacles.getLength();
        DebugHelper.group('障碍物统计', () => {
            DebugHelper.log(`总数: ${count}`);

            if (count === 0) {
                DebugHelper.warn('警告：没有创建任何障碍物！');
            } else if (count < 5) {
                DebugHelper.warn('警告：障碍物数量较少，可能没有正确创建');
            }
        });
    }

    getWorldBounds() {
        return this.worldBounds;
    }

    destroy() {
        if (this.isChunked) {
            this.unloadAllChunks();
        }

        if (this.obstacles) {
            this.obstacles.clear(true, true);
            this.obstacles = null;
        }

        if (this.debugLayer) {
            this.debugLayer.destroy();
            this.debugLayer = null;
        }

        // 清理背景 TileSprite
        if (this.backgroundTileSprite) {
            this.backgroundTileSprite.destroy();
            this.backgroundTileSprite = null;
        }

        this.worldBounds = null;
    }
}